import { useLayoutEffect, useRef, useState } from "react";
import SectionHeading from "@/components/SectionHeading";
import SubSectionHeading from "@/components/SubSectionHeading";
import Divider from "@/components/Divider";
import Avatar from "@/components/Avatar";
import Button from "@/components/Button";
import { SectionHeadingSkeleton, SkeletonElement } from "@/components/Skeleton";
import { MentionableTextField } from "@/components/AtMention";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import Comment from "./Comment";
import DeleteCommentModal from "./DeleteCommentModal";
import type { CommentData, CommentAuthor } from "./types";

interface Props {
  comments: CommentData[];
  heading?: string;
  headingVariant?: "section" | "subSection";
  currentUser?: CommentAuthor;
  onSaveEdit?: (id: string, content: string) => void | Promise<void>;
  onAddComment?: (content: string) => void;
  onDelete?: (id: string) => boolean | Promise<boolean>;
  loading?: boolean;
  compact?: boolean;
}

const FOCUS_SUPPRESS_MS = 300;

function findScrollableAncestor(el: HTMLElement): HTMLElement | Window {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const style = getComputedStyle(node);
    const overflowY = style.overflowY;
    const canScroll = (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight;
    if (canScroll) return node;
    node = node.parentElement;
  }
  return window;
}

export default function CommentList({
  comments,
  heading = "Comments",
  headingVariant = "section",
  currentUser,
  onSaveEdit,
  onAddComment,
  onDelete,
  loading,
  compact = false
}: Props) {
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [newComment, setNewComment] = useState("");
  const [isAddFocused, setIsAddFocused] = useState(false);
  const { Menu: ActionMenu, setMenu } = useTableRowMenu();

  const handleOpenMenu = (commentId: string, actions: Action[]) => {
    setMenu({ rowId: commentId, actions });
  };

  const commentRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const addRef = useRef<HTMLDivElement | null>(null);
  const scrollAnchor = useRef<{
    el: HTMLElement;
    top: number;
    releaseAt: number;
  } | null>(null);
  const suppressAddFocusUntilRef = useRef<number>(0);

  const suppressAddFocus = (durationMs: number = FOCUS_SUPPRESS_MS) => {
    suppressAddFocusUntilRef.current = performance.now() + durationMs;
  };

  const registerCommentRef = (id: string) => (el: HTMLDivElement | null) => {
    if (el) {
      commentRefs.current.set(id, el);
    } else {
      commentRefs.current.delete(id);
    }
  };

  const captureAnchor = (el: HTMLElement | null, holdMs: number = 0) => {
    if (!el) return;
    scrollAnchor.current = {
      el,
      top: el.getBoundingClientRect().top,
      releaseAt: performance.now() + holdMs
    };
  };

  useLayoutEffect(() => {
    const anchor = scrollAnchor.current;
    if (!anchor || !anchor.el.isConnected) return;
    const nextTop = anchor.el.getBoundingClientRect().top;
    const delta = nextTop - anchor.top;
    if (Math.abs(delta) > 0.5) {
      const scroller = findScrollableAncestor(anchor.el);
      if (scroller === window) {
        window.scrollBy({ top: delta, behavior: "auto" });
      } else if (scroller instanceof HTMLElement) {
        scroller.scrollTop += delta;
      }
      anchor.top = anchor.el.getBoundingClientRect().top;
    }
    if (performance.now() >= anchor.releaseAt) {
      scrollAnchor.current = null;
    }
  });

  const handleEdit = (id: string) => {
    setEditingCommentId(id);
  };

  const handleSave = async (id: string, content: string) => {
    captureAnchor(commentRefs.current.get(id) ?? null, 2000);
    await onSaveEdit?.(id, content);
    setEditingCommentId(null);
  };

  const handleCancel = () => {
    setEditingCommentId(null);
  };

  const handleDeleteClick = (id: string) => {
    setDeletingCommentId(id);
  };

  const handleDeleteConfirm = async (): Promise<boolean> => {
    if (!deletingCommentId || !onDelete) return false;
    return !!(await onDelete(deletingCommentId));
  };

  const handleDeleteClosed = () => {
    setDeletingCommentId(null);
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    captureAnchor(addRef.current, 5000);
    onAddComment?.(newComment.trim());
    suppressAddFocus();
    setNewComment("");
    setIsAddFocused(false);
  };

  const handleAddCancel = () => {
    suppressAddFocus();
    setNewComment("");
    setIsAddFocused(false);
  };

  if (loading) {
    return (
      <div data-testid="comment-list-loading">
        <SectionHeadingSkeleton />
        <div className="flex flex-col gap-4 py-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3">
              <SkeletonElement width="w-10" height="h-10" rounded="rounded-full" className="flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <SkeletonElement width="w-1/4" height="h-4" />
                <SkeletonElement width="w-full" height="h-3" />
                <SkeletonElement width="w-1/6" height="h-3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isSubSection = headingVariant === "subSection";

  return (
    <div data-testid="comment-list" className={`w-full ${isSubSection ? "flex flex-col gap-2 items-start" : ""}`}>
      {isSubSection ? <SubSectionHeading>{heading}</SubSectionHeading> : <SectionHeading heading={heading} />}

      <div className={compact ? "space-y-4 w-full" : undefined}>
        {comments.map((comment, index) => (
          <div key={comment.id} ref={registerCommentRef(comment.id)}>
            <Comment
              comment={comment}
              isEditing={editingCommentId === comment.id}
              canEdit={!!currentUser && currentUser.id === comment.author.id && !!onSaveEdit}
              canDelete={!!currentUser && currentUser.id === comment.author.id && !!onDelete}
              compact={compact}
              onEdit={handleEdit}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={handleDeleteClick}
              onOpenMenu={handleOpenMenu}
            />
            {!compact && index < comments.length - 1 && <Divider />}
          </div>
        ))}
      </div>

      {onAddComment && (
        <>
          {comments.length > 0 && <Divider />}
          <div
            ref={addRef}
            className={`flex gap-3 pb-4 ${comments.length > 0 ? "pt-6" : ""}`}
            data-testid="comment-new-input"
          >
            <div className="flex-shrink-0 pt-0.5">
              <Avatar picture={currentUser?.photo} size="medium" />
            </div>
            <div
              className="flex-1 flex flex-col gap-3"
              onFocusCapture={() => {
                if (performance.now() < suppressAddFocusUntilRef.current) return;
                setIsAddFocused(true);
              }}
              data-testid="comment-new-input-body"
            >
              <MentionableTextField
                value={newComment}
                onChange={setNewComment}
                rows={1}
                label="Add a Comment"
                placeholder="Add a comment..."
                className="w-full"
                data-testid="comment-new-field"
              />
              {isAddFocused && (
                <div className="flex gap-2 justify-end">
                  <Button variant="text" onClick={handleAddCancel} testId="comment-add-cancel-button">
                    Cancel
                  </Button>
                  <Button
                    variant="filled"
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    testId="comment-add-save-button"
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <ActionMenu />

      {deletingCommentId && onDelete && (
        <DeleteCommentModal onConfirm={handleDeleteConfirm} onClosed={handleDeleteClosed} />
      )}
    </div>
  );
}
